import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box, Typography, alpha } from '@mui/material';
import { palette } from '../../theme';

const STATUS_COLOR = {
  online: palette.success,
  offline: palette.danger,
  updating: palette.warning,
  warning: palette.warning,
};

/**
 * Interactive 3D device cluster.
 *
 * Devices are distributed across a rotating 3D lattice. The scene supports
 * drag-to-rotate, wheel zoom, hover inspection and click selection.
 */
export default function DeviceCluster3D({
  devices = [],
  selectedId,
  onSelect,
  height = 420,
  onHover,
}) {
  const [rotation, setRotation] = useState({ x: -18, y: 24 });
  const [zoom, setZoom] = useState(1);
  const [dragging, setDragging] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const dragOrigin = useRef({ x: 0, y: 0 });
  const rotationOrigin = useRef({ x: -18, y: 24 });

  const positions = useMemo(() => {
    const items = devices.slice(0, 48);
    const columns = Math.ceil(Math.sqrt(items.length)) || 1;
    return items.map((device, index) => {
      const row = Math.floor(index / columns);
      const column = index % columns;
      const spacing = 84;
      const offsetX = (column - (columns - 1) / 2) * spacing;
      const offsetZ = (row - (Math.ceil(items.length / columns) - 1) / 2) * spacing;
      return {
        ...device,
        x: offsetX,
        z: offsetZ,
        y: (row % 2) * 14 - 7,
        delay: (index % 6) * 0.28,
      };
    });
  }, [devices]);

  const onPointerDown = useCallback((event) => {
    setDragging(true);
    dragOrigin.current = { x: event.clientX, y: event.clientY };
    rotationOrigin.current = rotation;
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [rotation]);

  const onPointerMove = useCallback((event) => {
    if (!dragging) return;
    const deltaX = event.clientX - dragOrigin.current.x;
    const deltaY = event.clientY - dragOrigin.current.y;
    setRotation({
      x: Math.max(-55, Math.min(35, rotationOrigin.current.x - deltaY * 0.24)),
      y: rotationOrigin.current.y + deltaX * 0.3,
    });
  }, [dragging]);

  const onPointerUp = useCallback((event) => {
    setDragging(false);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }, []);

  const onWheel = useCallback((event) => {
    event.preventDefault();
    setZoom((current) => Math.max(0.55, Math.min(1.7, current - event.deltaY * 0.0012)));
  }, []);

  const handleHover = (device) => {
    setHoveredId(device.device_id);
    onHover?.(device);
  };

  return (
    <Box
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onWheel={onWheel}
      sx={{
        position: 'relative',
        height,
        perspective: 900,
        cursor: dragging ? 'grabbing' : 'grab',
        touchAction: 'none',
        overflow: 'hidden',
        borderRadius: 3,
        background: `radial-gradient(120% 90% at 50% 0%, ${alpha(palette.primary, 0.1)}, transparent 62%)`,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          transformStyle: 'preserve-3d',
          transform: `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg) scale(${zoom})`,
          transition: dragging ? 'none' : 'transform 420ms cubic-bezier(0.22, 0.9, 0.28, 1)',
        }}
      >
        {/* floor lattice */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: 620,
            height: 620,
            transform: 'translate(-50%, -50%) rotateX(78deg)',
            backgroundImage: `
              linear-gradient(${alpha(palette.primary, 0.16)} 1px, transparent 1px),
              linear-gradient(90deg, ${alpha(palette.primary, 0.16)} 1px, transparent 1px)
            `,
            backgroundSize: '52px 52px',
            maskImage: 'radial-gradient(circle at 50% 50%, black 20%, transparent 72%)',
            WebkitMaskImage: 'radial-gradient(circle at 50% 50%, black 20%, transparent 72%)',
            opacity: 0.85,
          }}
        />
        {/* horizon glow */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: 520,
            height: 520,
            transform: 'translate(-50%, -50%) rotateX(78deg)',
            background: `radial-gradient(circle, ${alpha(palette.primary, 0.16)}, transparent 62%)`,
            filter: 'blur(20px)',
          }}
        />

        {positions.map((device) => {
          const color = STATUS_COLOR[device.status] || palette.secondaryText;
          const isSelected = selectedId === device.device_id;
          const isHovered = hoveredId === device.device_id;
          const scale = isSelected ? 1.35 : isHovered ? 1.2 : 1;

          return (
            <Box
              key={device.device_id}
              onClick={(event) => {
                event.stopPropagation();
                onSelect?.(device);
              }}
              onMouseEnter={() => handleHover(device)}
              onMouseLeave={() => setHoveredId(null)}
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: 30,
                height: 30,
                marginLeft: -15,
                marginTop: -15,
                transformStyle: 'preserve-3d',
                transform: `translate3d(${device.x}px, ${device.y}px, ${device.z}px)`,
                cursor: 'pointer',
              }}
            >
              {/* node body */}
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: 2.5,
                  background: `linear-gradient(150deg, ${alpha(color, 0.55)}, ${alpha('#0A1122', 0.9)})`,
                  border: `1px solid ${alpha(color, 0.75)}`,
                  boxShadow: `0 0 ${isSelected ? 26 : 14}px ${alpha(color, isSelected ? 0.85 : 0.45)}`,
                  transform: `scale(${scale})`,
                  transition: 'transform 260ms cubic-bezier(0.22, 0.9, 0.28, 1), box-shadow 260ms ease',
                  animation: `holoPulse ${3 + (device.delay % 3)}s ease-in-out ${device.delay}s infinite`,
                }}
              />
              {/* base shadow */}
              <Box
                sx={{
                  position: 'absolute',
                  left: 4,
                  right: 4,
                  bottom: -14,
                  height: 10,
                  borderRadius: '50%',
                  background: `radial-gradient(closest-side, ${alpha(color, 0.5)}, transparent)`,
                  filter: 'blur(5px)',
                  transform: 'rotateX(80deg)',
                }}
              />
              {/* label */}
              {isHovered && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 38,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    whiteSpace: 'nowrap',
                    px: 1,
                    py: 0.4,
                    borderRadius: 1.5,
                    background: alpha('#020617', 0.9),
                    border: `1px solid ${alpha(color, 0.4)}`,
                  }}
                >
                  <Typography className="mono" sx={{ fontSize: '0.6rem', color: color }}>
                    {device.device_id}
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {devices.length === 0 && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
            No devices registered in this view
          </Typography>
        </Box>
      )}

      <Typography
        sx={{
          position: 'absolute',
          left: 14,
          bottom: 12,
          fontSize: '0.64rem',
          color: 'text.secondary',
          letterSpacing: '0.1em',
          pointerEvents: 'none',
        }}
      >
        DRAG TO ROTATE · SCROLL TO ZOOM · CLICK TO INSPECT
      </Typography>

      <Box
        sx={{
          position: 'absolute',
          right: 14,
          top: 14,
          display: 'flex',
          gap: 1.5,
          pointerEvents: 'none',
        }}
      >
        {Object.entries(STATUS_COLOR).slice(0, 3).map(([key, color]) => (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color, boxShadow: `0 0 8px ${color}` }} />
            <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              {key}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
